import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import MessageContext, {
    vMsgProps,
    initMessageProps,
} from "./Context/MessageContext";
import LoginInfoContext, { InitRoleData } from "./Context/LoginInfoContext";
import {
    loginUserApi,
    vTokenData,
    vSystemType,
    role,
    LoginInput,
} from "./utils/GeneBoxAPI";
import axios from "axios";
import jwtDecode from "jwt-decode";

import Login from "./Login";
import Head from "./Head/Head";
import Logout from "./Head/Logout";
import SystemType from "./Head/SystemType";
import ResetPassword from "./Head/ResetPassword";
import Tab from "./component/Tab";
import Loading from "./component/Loading";
import Message from "./component/Message";
import Redirect from "./component/Redirect";

import routeInfo, {
    addUserList,
    newRouteInfo,
    returnRouteInfo,
    RouteType,
} from "./router";
import { deepEqualObject } from "./utils/DataProcessor";

const App = () => {
    //login
    const bAuth = useRef(false);
    const currentSession = useRef("");
    const sSystem = useRef(vSystemType.blank);

    const [loginState, setLoginState] = useState(false); // 是否登入
    const [sCurrentToken, setCurrentToken] = useState(""); // 系統 token
    const [bLoading, setbLoading] = useState(false);
    const [sOrgIdx, setsOrgIdx] = useState(""); // 使用者名字???
    const [isAdmin, setIsAdmin] = useState(true); // 是否為系統管理員
    const [roleData, setRoleData] = useState<role>(InitRoleData);

    // key : vSystemType note: 加上權限
    const [vSystemInfo, setSystemInfo] = useState<{
        [x: string]: { type: vSystemType; text: string; authorize: boolean };
    }>({
        genebox: {
            type: vSystemType.genebox,
            text: "基因寶",
            authorize: false,
        },
        gutflora: {
            type: vSystemType.gutflora,
            text: "腸菌寶",
            authorize: false,
        },
        nips: {
            type: vSystemType.nips,
            text: "NIPS",
            authorize: false,
        },
    });

    const [allRouteInfo, setallRouteInfo] = useState<RouteType[]>(
        newRouteInfo()
    );

    const [isAlert, setIsAlert] = useState(false); // 是否展示提示框
    const [alertProps, setAlertProps] = useState<vMsgProps>(initMessageProps); // 提示框內容

    // *** localStorage start *** //
    const saveRefreshToken = (credentials: LoginInput) => {
        window.localStorage.setItem(
            "currentSession",
            JSON.stringify(credentials)
        );
    };

    const removeRefreshToken = () => {
        window.localStorage.removeItem("currentSession");
    };
    // *** localStorage end *** //

    const login = (credentials: LoginInput) => {
        loginUserApi(credentials)
            .then(({data}) => {
                const token = data.token;
                if (!token) {
                    throw new Error("Token is not present in the response");
                }

                try {
                    const userToken: vTokenData = jwtDecode(token);
                    if (data.role_data.projects) {
                        setRoleData(data.role_data);
                        setIsAdmin(data.is_admin);
                        setLoginState(true);
                        bAuth.current = true;
                        setCurrentToken(token);
                        setbLoading(false);
                        setsOrgIdx(userToken.display_name);
                        saveRefreshToken(credentials);

                        axios.defaults.headers.common[
                            "Authorization"
                        ] = `Bearer ${token}`;
                    } else {
                        setbLoading(false);
                        setIsAlert(true);
                        setAlertProps({
                            ...alertProps,
                            title: "權限不足",
                            iMsg: 1,
                        });
                        logoutCallback();
                    }
                } catch (decodeError) {
                    console.error("Failed to decode token:", decodeError);
                    throw new Error("Invalid token format");
                }
            })
            .catch(() => {
                setbLoading(false);
                setIsAlert(true);
                setAlertProps({
                    ...alertProps,
                    title: "登入失敗",
                    iMsg: 1,
                });
                logoutCallback();
            });
    };

    const loginCallback = async (credentials: LoginInput) => {
        setbLoading(true);
        await login(credentials);
    };

    // logout
    const logoutCallback = () => {
        bAuth.current = false;
        currentSession.current = "";
        setLoginState(false);
        setCurrentToken("");
        sSystem.current = vSystemType.blank;
        removeRefreshToken();
        setallRouteInfo(routeInfo); //還原最初的router
        setbLoading(false);
        setIsAdmin(false);
    };

    // 處理 axios catch
    const errorProcess = (errorStatus?: number, errorMes?: Object) => {
        setIsAlert(true);
        setAlertProps({
            ...alertProps,
            title: "載入失敗",
            iMsg: 1,
        });
    };

    const getUrlDefault = (roleData: role) => {
        if (sSystem.current === 0 || !bAuth.current) return "";
        let url = "";
        const vendorKeys = Object.keys(roleData.projects.genebox.vendor || {});
        for (const key of vendorKeys) {
            if (!roleData.projects[vSystemType[sSystem.current]]?.vendor[key])
                continue;
            if (
                roleData.projects[vSystemType[sSystem.current]].vendor[key]
                    .worklist
            ) {
                if (url === "")
                    url = `/specimen/${key.toLowerCase()}/${
                        vSystemType[sSystem.current]
                    }`;
            } else if (
                roleData.projects[vSystemType[sSystem.current]].vendor[key]
                    .appointed
            ) {
                if (url === "")
                    url = `/report_list/${key.toLowerCase()}/${
                        vSystemType[sSystem.current]
                    }`;
            }
        }
        if (url === "") {
            if (roleData.projects[vSystemType[sSystem.current]].upload) {
                url = `/upload/${vSystemType[sSystem.current]}`;
            } else if (
                roleData.projects[vSystemType[sSystem.current]].report_exception
            ) {
                url = `/except/${vSystemType[sSystem.current]}`;
            } else if (
                roleData.projects[vSystemType[sSystem.current]].pmc_sign
            ) {
                url = `/pmc_sign/${vSystemType[sSystem.current]}`;
            } else if (
                roleData.projects[vSystemType[sSystem.current]].doctor_sign
            ) {
                url = `/doctor_sign/${vSystemType[sSystem.current]}`;
            } else {
                logoutCallback();
            }
        }
        return url;
    };

    useEffect(() => {
        const currentSessionLocal =
            window.localStorage.getItem("currentSession");
        if (currentSession.current === "" && currentSessionLocal === null) {
            logoutCallback();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSession]);

    const getNewSystem = () => {
        //改寫拿取可以的系統 也就是authorize 為 true的
        const vAuthorizeType = Object.keys(roleData.projects).filter((item) => {
            return roleData.projects[item].Access === true;
        });
        if (vAuthorizeType.length === 0) return;

        let sNewSystem = vSystemInfo[vAuthorizeType[0]].type;
        if (vAuthorizeType.includes(vSystemType[1])) {
            sNewSystem = vSystemInfo[vSystemType[1]].type;
        } else if (vAuthorizeType.includes(vSystemType[2])) {
            sNewSystem = vSystemInfo[vSystemType[2]].type;
        } else if (vAuthorizeType.includes(vSystemType[3])) {
            sNewSystem = vSystemInfo[vSystemType[3]].type;
        }

        sSystem.current = sNewSystem;
    };

    const MessageProvider = {
        alertProps,
        setAlertProps,
        setIsAlert,
    };

    const vLoginProvider = {
        roleData: roleData,
        isHidden: loginState,
        sCurrentToken,
        sOrgIdx,
        isAdmin,
        sSystem,
        setbLoading,
        setCurrentToken,
        loginCallback,
        logoutCallback,
        errorProcess,
        getNewSystem,
        getUrlDefault,
    };

    const getTabListLength = (routeInfo: RouteType[]) => {
        const vTabLi = routeInfo.filter((route) => {
            return (
                route.bTab &&
                route.authorize.includes(vSystemType[sSystem.current])
            );
        });
        return vTabLi.length;
    };

    useEffect(() => {
        const currentSessionLocal =
            window.localStorage.getItem("currentSession");
        if (currentSessionLocal !== null) {
            const credentials = JSON.parse(currentSessionLocal);
            if (credentials.acc && credentials.pwd) {
                loginCallback(credentials);
            }
        }
        getNewSystem();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (roleData === undefined) return;
        if (deepEqualObject(roleData, InitRoleData)) {
            return;
        }
        const InitRoute = returnRouteInfo(roleData);
        const vDealerRouter = addUserList(roleData);
        const tempRouterInfo = InitRoute.concat(vDealerRouter);

        setallRouteInfo((prev) => {
            let temp = prev || [];
            temp = tempRouterInfo;
            // console.log("temp", temp);
            return temp;
        });
        setSystemInfo((prev) => {
            let newSys = Object.assign({}, prev);
            newSys.genebox.authorize = roleData.projects.genebox.Access;
            newSys.gutflora.authorize = roleData.projects.gutflora.Access;
            newSys.nips.authorize = roleData.projects.nips.Access;
            return newSys;
        });
        getNewSystem();
        getUrlDefault(roleData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleData]);

    return (
        <LoginInfoContext.Provider value={vLoginProvider}>
            <MessageContext.Provider value={MessageProvider}>
                <Router>
                    <Switch>
                        {allRouteInfo.map((route, i) => {
                            if (!bAuth.current || roleData === undefined) {
                                return (
                                    <Route
                                        key={i}
                                        render={() => (
                                            <>
                                                <Head subject="">
                                                    <Logout />
                                                </Head>
                                                <Login />
                                            </>
                                        )}
                                    ></Route>
                                );
                            }
                            const { path, exact, bTab } = route;
                            let routeComponents: JSX.Element = <></>;
                            let TabLength = getTabListLength(allRouteInfo);
                            if (!bTab || TabLength === 1) {
                                routeComponents = <route.component />;
                            } else {
                                routeComponents = (
                                    <>
                                        <Tab allRouteInfo={allRouteInfo} />
                                        <div className="tab_content_1">
                                            <route.component />
                                        </div>
                                    </>
                                );
                            }
                            return (
                                <Route
                                    key={i}
                                    path={path}
                                    exact={exact}
                                    render={() => (
                                        <>
                                            <Head subject="">
                                                <>
                                                    <SystemType
                                                        SystemInfo={vSystemInfo}
                                                    />
                                                    <ResetPassword />
                                                    <Logout />
                                                </>
                                            </Head>
                                            <div className="container tab_container">
                                                {routeComponents}
                                            </div>
                                        </>
                                    )}
                                />
                            );
                        })}

                        {/* 404 */}
                        <Route path="*">
                            <Head subject="">
                                <Logout />
                            </Head>
                            <Redirect />
                        </Route>
                    </Switch>
                </Router>
                {isAlert && <Message {...alertProps} />}
                {bLoading && <Loading />}
            </MessageContext.Provider>
        </LoginInfoContext.Provider>
    );
};

export default App;
